import { MsalProvider } from "@azure/msal-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { scan } from "react-scan";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import App from "./App.tsx";
import { msalInitPromise, msalInstance } from "./lib/config/authConfig.ts";
import queryClient from "./lib/config/queryClient.ts";
import store from "./lib/store/dynamicStore.ts";
import "./index.css";
import TBHLogo from "@/einstein/components/common/TBHLogo.tsx";
import "./app.scss";
import {
	ReactPlugin,
	withAITracking,
} from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { createBrowserHistory } from "history";
import { Toaster } from "react-hot-toast";

// --- Application Insights Setup ---
const browserHistory = createBrowserHistory();
const reactPlugin = new ReactPlugin();

// Check if we're in development mode
const isDevelopment =
	import.meta.env.DEV || import.meta.env.VITE_CUSTOM_ENV_NAME === "development";

const appInsights = new ApplicationInsights({
	config: {
		connectionString: import.meta.env
			.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING,
		extensions: [reactPlugin],
		extensionConfig: {
			[reactPlugin.identifier]: { history: browserHistory },
		},
		enableAutoRouteTracking: !isDevelopment, // Disable route tracking in development
		autoTrackPageVisitTime: !isDevelopment, // Disable page visit time tracking in development
		disableTelemetry: isDevelopment, // Completely disable telemetry in development
	},
});

// Only enable React Scan in development mode
scan({
	enabled: false,
});

// Only enable Application Insights in production and when connection string is provided
const shouldEnableTelemetry =
	!isDevelopment && import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING;

if (shouldEnableTelemetry) {
	appInsights.loadAppInsights();
	console.log("Application Insights enabled for production environment.");
} else {
	if (isDevelopment) {
		console.log("Application Insights disabled in development mode.");
	} else {
		console.warn(
			"Application Insights connection string not provided. Telemetry disabled.",
		);
	}
}

// Create a component that conditionally includes AI tracking based on environment
const AiTrackedApp = shouldEnableTelemetry
	? withAITracking(reactPlugin, App, "App")
	: App;
// --- End Application Insights Setup ---

const container = document.getElementById("root");

const deploymentEnv = import.meta.env.VITE_CUSTOM_ENV_NAME;

// Initialize the app after MSAL is ready
const initializeApp = async () => {
	// Wait for MSAL to initialize
	await msalInitPromise;

	if (container) {
		const root = createRoot(container);
		root.render(
			<React.StrictMode>
				<QueryClientProvider client={queryClient}>
					<MsalProvider instance={msalInstance}>
						<Toaster
							position="top-right"
							containerStyle={{ top: "100px" }}
							toastOptions={{
								style: {
									borderRadius: "24px",
									padding: "10px",
									fontSize: "16px",
									fontFamily: "Rubik",
								},
							}}
						/>
						<Provider store={store}>
							<BrowserRouter>
								<QueryParamProvider adapter={ReactRouter6Adapter}>
									<AiTrackedApp />
									<TBHLogo />
								</QueryParamProvider>
							</BrowserRouter>
						</Provider>
					</MsalProvider>
					{deploymentEnv === "development" && (
						<ReactQueryDevtools initialIsOpen={false} position="bottom" />
					)}
				</QueryClientProvider>
			</React.StrictMode>,
		);
	}
};

// Start the application
initializeApp();
